import React from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useWizard, WizardStepLayout } from 'components/layout/wizards';
import {
  DateField,
  DateRangeToggle,
  MonthDaySelector,
  TimeField,
  WeekDaySelector,
} from 'components/forms';
import { selectSettings } from 'store/slices/authSlice';
import {
  REMINDER_FREQUENCIES,
  REMINDER_RANGE_FIELD_KEYS,
  REMINDER_RANGE_SUPPORTED_FREQUENCIES,
} from 'constants/reminders';
import { TIME_FORMATS } from 'constants/date';
import { isValidMonthDay } from 'utils/date';
import { hasSeasonalRange } from 'utils/reminders';

function getRangeValidationMessage(label, month, day, yearRound) {
  if (yearRound) {
    return true;
  }

  if (month == null || day == null) {
    return `${label} date is required`;
  }

  if (!isValidMonthDay(month, day)) {
    return 'Invalid day for selected month';
  }

  return true;
}

export default function RepeatingScheduleStep() {
  const { nextStep, data } = useWizard();
  const settings = useSelector(selectSettings);
  const { frequency } = data;
  const supportsSeasonalRange = REMINDER_RANGE_SUPPORTED_FREQUENCIES.includes(frequency);

  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      time: data.schedule?.time || '09:00',
      daysOfWeek: data.schedule?.daysOfWeek || [],
      daysOfMonth: data.schedule?.daysOfMonth || [],
      executionDate: data.schedule?.executionDate || null,
      yearRound: supportsSeasonalRange ? !hasSeasonalRange(data.schedule) : true,
      rangeStartMonth: data.schedule?.rangeStartMonth ?? null,
      rangeStartDay: data.schedule?.rangeStartDay ?? null,
      rangeEndMonth: data.schedule?.rangeEndMonth ?? null,
      rangeEndDay: data.schedule?.rangeEndDay ?? null,
    },
  });

  const [
    yearRound,
    rangeStartMonth,
    rangeStartDay,
    rangeEndMonth,
    rangeEndDay,
  ] = useWatch({
    control,
    name: [
      'yearRound',
      'rangeStartMonth',
      'rangeStartDay',
      'rangeEndMonth',
      'rangeEndDay',
    ],
  });

  const rangeFieldNames = [
    'rangeStartMonth',
    'rangeStartDay',
    'rangeEndMonth',
    'rangeEndDay',
  ];

  const onSubmit = (formData) => {
    const scheduleData = { ...data.schedule };

    REMINDER_RANGE_FIELD_KEYS.forEach((key) => {
      delete scheduleData[key];
    });

    if (frequency === REMINDER_FREQUENCIES.DAILY) {
      scheduleData.time = formData.time;
    } else if (frequency === REMINDER_FREQUENCIES.WEEKLY) {
      scheduleData.time = formData.time;
      scheduleData.daysOfWeek = formData.daysOfWeek;
    } else if (frequency === REMINDER_FREQUENCIES.MONTHLY) {
      scheduleData.time = formData.time;
      scheduleData.daysOfMonth = formData.daysOfMonth;
    } else if (frequency === REMINDER_FREQUENCIES.ANNUAL) {
      scheduleData.executionDate = formData.executionDate;
    }

    if (supportsSeasonalRange && !formData.yearRound) {
      scheduleData.rangeStartMonth = formData.rangeStartMonth;
      scheduleData.rangeStartDay = formData.rangeStartDay;
      scheduleData.rangeEndMonth = formData.rangeEndMonth;
      scheduleData.rangeEndDay = formData.rangeEndDay;
    }

    nextStep({ schedule: scheduleData });
  };

  const handleYearRoundChange = (checked) => {
    setValue('yearRound', checked, {
      shouldDirty: true,
      shouldTouch: true,
    });

    if (checked) {
      clearErrors(rangeFieldNames);
    }
  };

  const handleRangeChange = (nextRange) => {
    const nextValues = {
      rangeStartMonth,
      rangeStartDay,
      rangeEndMonth,
      rangeEndDay,
      ...nextRange,
    };

    Object.entries(nextRange).forEach(([key, value]) => {
      setValue(key, value, {
        shouldDirty: true,
        shouldTouch: true,
      });
    });

    if (getRangeValidationMessage('Start', nextValues.rangeStartMonth, nextValues.rangeStartDay, yearRound) === true) {
      clearErrors(['rangeStartMonth', 'rangeStartDay']);
    }

    if (getRangeValidationMessage('End', nextValues.rangeEndMonth, nextValues.rangeEndDay, yearRound) === true) {
      clearErrors(['rangeEndMonth', 'rangeEndDay']);
    }
  };

  const startRangeError = errors.rangeStartMonth?.message || errors.rangeStartDay?.message;
  const endRangeError = errors.rangeEndMonth?.message || errors.rangeEndDay?.message;

  const renderDateRangeToggle = () => {
    if (!supportsSeasonalRange) {
      return null;
    }

    return (
      <>
        <Controller
          name="yearRound"
          control={control}
          render={() => null}
        />
        <Controller
          name="rangeStartMonth"
          control={control}
          rules={{
            validate: () => getRangeValidationMessage('Start', rangeStartMonth, rangeStartDay, yearRound),
          }}
          render={() => null}
        />
        <Controller
          name="rangeStartDay"
          control={control}
          rules={{
            validate: () => getRangeValidationMessage('Start', rangeStartMonth, rangeStartDay, yearRound),
          }}
          render={() => null}
        />
        <Controller
          name="rangeEndMonth"
          control={control}
          rules={{
            validate: () => getRangeValidationMessage('End', rangeEndMonth, rangeEndDay, yearRound),
          }}
          render={() => null}
        />
        <Controller
          name="rangeEndDay"
          control={control}
          rules={{
            validate: () => getRangeValidationMessage('End', rangeEndMonth, rangeEndDay, yearRound),
          }}
          render={() => null}
        />
        <DateRangeToggle
          className="pt-2"
          yearRound={yearRound}
          onYearRoundChange={handleYearRoundChange}
          startMonth={rangeStartMonth}
          startDay={rangeStartDay}
          endMonth={rangeEndMonth}
          endDay={rangeEndDay}
          onChange={handleRangeChange}
          errors={{
            start: startRangeError,
            end: endRangeError,
          }}
        />
      </>
    );
  };

  const renderContent = () => {
    switch (frequency) {
      case REMINDER_FREQUENCIES.DAILY:
        return (
          <div className="space-y-6 max-w-md mx-auto">
            <div>
              <Controller
                name="time"
                control={control}
                rules={{ required: 'Time is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TimeField
                    label="Time"
                    error={error?.message}
                    use12HourFormat={settings?.timeFormat === TIME_FORMATS.H12}
                    {...field}
                  />
                )}
              />
            </div>
            {renderDateRangeToggle()}
          </div>
        );
      case REMINDER_FREQUENCIES.WEEKLY:
        return (
          <div className="space-y-6 max-w-md mx-auto">
            <Controller
              name="daysOfWeek"
              control={control}
              rules={{ required: 'Select at least one day' }}
              render={({ field, fieldState: { error } }) => (
                <WeekDaySelector
                  label="Days of Week"
                  error={error?.message}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <div className="max-w-md mx-auto">
              <Controller
                name="time"
                control={control}
                rules={{ required: 'Time is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TimeField
                    label="Time"
                    error={error?.message}
                    use12HourFormat={settings?.timeFormat === TIME_FORMATS.H12}
                    {...field}
                  />
                )}
              />
            </div>
            {renderDateRangeToggle()}
          </div>
        );
      case REMINDER_FREQUENCIES.MONTHLY:
        return (
          <div className="space-y-6 max-w-md mx-auto">
            <Controller
              name="daysOfMonth"
              control={control}
              rules={{ required: 'Day is required' }}
              render={({ field, fieldState: { error } }) => (
                <MonthDaySelector
                  label="Day of Month"
                  error={error?.message}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <div className="max-w-md mx-auto">
              <Controller
                name="time"
                control={control}
                rules={{ required: 'Time is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TimeField
                    label="Time"
                    error={error?.message}
                    use12HourFormat={settings?.timeFormat === TIME_FORMATS.H12}
                    {...field}
                  />
                )}
              />
            </div>
            {renderDateRangeToggle()}
          </div>
        );
      case REMINDER_FREQUENCIES.ANNUAL:
        return (
          <div className="max-w-md mx-auto">
            <Controller
              name="executionDate"
              control={control}
              rules={{ required: 'Date is required' }}
              render={({ field, fieldState: { error } }) => (
                <DateField
                  label="Date & Time (Year ignored)"
                  showTime
                  hideYear
                  dateFormat={settings?.dateFormat}
                  timeFormat={settings?.timeFormat}
                  error={error?.message}
                  {...field}
                />
              )}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <WizardStepLayout
        title="Configure Schedule"
        description="Set the details for your repeating reminder."
        primaryAction={{ label: 'Next', type: 'submit' }}
      >
        {renderContent()}
      </WizardStepLayout>
    </form>
  );
}
